import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import type { TIssueQuery } from "@/types/issue";
import type {
  TIssueDisplayFilters,
  TIssueDisplayProperties,
  TIssueFilterState,
  TIssueLayout,
  TIssueOrderBy,
} from "@/types/issue-filter";
import type {
  TIssueCollectionKey,
  TIssueCollectionScope,
  TSavedViewSnapshot,
  TStaticWorkspaceViewId,
} from "@/types/view";

export const STATIC_WORKSPACE_VIEW_IDS: TStaticWorkspaceViewId[] = [
  "all-issues",
  "assigned",
  "created",
];

const DEFAULT_RICH_FILTER_KEYS = {
  search: "search",
  state: "state",
  assignees: "assignees",
  priority: "priority",
  targetDate: "target_date",
} as const;

function isIssueLayout(value: unknown): value is TIssueLayout {
  return (
    value === "list" ||
    value === "board" ||
    value === "calendar" ||
    value === "gantt" ||
    value === "timeline"
  );
}

function isIssueOrderBy(value: unknown): value is TIssueOrderBy {
  return (
    value === "-created_at" ||
    value === "created_at" ||
    value === "-updated_at" ||
    value === "updated_at" ||
    value === "sequence_id" ||
    value === "-sequence_id" ||
    value === "name" ||
    value === "-name" ||
    value === "priority" ||
    value === "-priority" ||
    value === "start_date" ||
    value === "-start_date" ||
    value === "target_date" ||
    value === "-target_date"
  );
}

export function normalizeArrayValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry)).sort();
  }

  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean)
      .sort();
  }

  return [];
}

function normalizePlainValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    const normalized = value.map((entry) => normalizePlainValue(entry));
    if (normalized.every((entry) => ["string", "number", "boolean"].includes(typeof entry))) {
      // Lexicographic sort via String coercion. Safe today because all current
      // filter primitives are UUID strings. Numeric filter authors must
      // canonicalize before passing through this helper or dirty checks will
      // misorder values like [3, 10, 1] -> ["1", "10", "3"].
      return [...normalized].sort((left, right) => String(left).localeCompare(String(right)));
    }
    return normalized;
  }

  if (value && typeof value === "object") {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = normalizePlainValue((value as Record<string, unknown>)[key]);
        return acc;
      }, {});
  }

  return value;
}

export function cleanQuery(query: Record<string, unknown>): TIssueQuery {
  return Object.entries(query).reduce<TIssueQuery>((acc, [key, value]) => {
    if (value === undefined || value === null || value === "") return acc;
    if (Array.isArray(value) && value.length === 0) return acc;
    acc[key] = value as TIssueQuery[string];
    return acc;
  }, {});
}

export function getIssueCollectionKey(scope: TIssueCollectionScope): TIssueCollectionKey {
  switch (scope.kind) {
    case "project":
      return `project:${scope.projectId}`;
    case "project-view":
      return `project-view:${scope.projectId}:${scope.viewId}`;
    case "workspace-view":
      return `workspace-view:${scope.viewId}`;
    case "module":
      return `module:${scope.moduleId}`;
    case "archived":
      return `archived:${scope.projectId}`;
  }
}

export function isStaticWorkspaceViewId(value: string): value is TStaticWorkspaceViewId {
  return STATIC_WORKSPACE_VIEW_IDS.includes(value as TStaticWorkspaceViewId);
}

const ACTIVE_FILTER_QUERY_KEYS = [
  "search",
  "state",
  "assignees",
  "priority",
  "target_date",
  "created_by",
  "subscriber",
] as const;

export function hasActiveIssueFilters(query: TIssueQuery): boolean {
  return ACTIVE_FILTER_QUERY_KEYS.some((key) => Boolean(query[key]));
}

export function getStaticWorkspaceViewFilters(
  currentUserId: string,
  viewId: TStaticWorkspaceViewId,
): TIssueQuery {
  switch (viewId) {
    case "assigned":
      return { assignees: currentUserId };
    case "created":
      return { created_by: currentUserId };
    case "subscribed":
      return { subscriber: currentUserId };
    default:
      return {};
  }
}

export function getDefaultIssueFilters(): TIssueFilterState {
  return {
    layout: "list",
    search: "",
    stateIds: [],
    assigneeIds: [],
    priorities: [],
    targetDate: "",
    targetDateOp: "before",
    targetDateTo: "",
    overdue: false,
    allocation: false,
    orderBy: "-created_at",
    groupBy: "none",
    boardGroupBy: "status",
    archived: false,
  };
}

export function getDefaultDisplayProperties(): TIssueDisplayProperties {
  return {
    show_assignee: true,
    show_identifier: true,
    show_priority: true,
    show_state: true,
    show_target_date: true,
  };
}

export function buildIssueQueryFromFilterState(filters: TIssueFilterState): TIssueQuery {
  const query: TIssueQuery = {};

  if (filters.search.trim()) query.search = filters.search.trim();
  if (filters.stateIds.length > 0) query.state = filters.stateIds.join(",");
  if (filters.assigneeIds.length > 0) query.assignees = filters.assigneeIds.join(",");
  if (filters.priorities.length > 0) query.priority = filters.priorities.join(",");
  // Ranges, not an exact match: the Filter menu offers before / after / between,
  // which map onto the server's `target_date_before` / `target_date_after` pair.
  // ("between" is inclusive on both ends, hence the same two params.)
  if (filters.targetDate) {
    if (filters.targetDateOp === "after") {
      query.target_date_after = filters.targetDate;
    } else if (filters.targetDateOp === "between") {
      query.target_date_after = filters.targetDate;
      if (filters.targetDateTo) query.target_date_before = filters.targetDateTo;
    } else {
      query.target_date_before = filters.targetDate;
    }
  }
  if (filters.overdue) query.overdue = "true";
  // `filters.allocation` is deliberately absent from the GENERIC workspace
  // query. Project work tabs add the server-owned parameter in
  // `buildProjectIssueQueryFromFilterState` below.
  if (filters.archived) query.archived = "true";
  if (filters.orderBy !== "-created_at") query.order_by = filters.orderBy;
  if (filters.layout === "board") query.group_by = "state";
  if (filters.layout === "calendar" && filters.orderBy === "-created_at")
    query.order_by = "target_date";
  // List (spreadsheet table) expands sub-items inline — exclude them from the
  // flat list so they only appear when their parent row is expanded.
  if (filters.layout === "list") query.sub_issue = false;

  return query;
}

/** Project work-tab query semantics for the two server-owned status-band facts.
 *
 * The generic `overdue` parameter stays task-only for dashboard KPI parity.
 * Project tabs instead use `project_overdue`, whose count/filter span tasks and
 * milestones at any depth. Either alert drilldown must also drop the List
 * layout's root-only `sub_issue=false`, or a nested match counted by the project
 * payload would disappear from the rows (NG-847).
 */
export function buildProjectIssueQueryFromFilterState(filters: TIssueFilterState): TIssueQuery {
  const query = buildIssueQueryFromFilterState(filters);
  if (filters.overdue) {
    delete query.overdue;
    query.project_overdue = "true";
  }
  if (filters.allocation) query.allocation = "true";
  if (filters.overdue || filters.allocation) delete query.sub_issue;
  return query;
}

export function extractFilterStateFromSavedView(snapshot: {
  rich_filters?: Record<string, unknown>;
  display_filters?: TIssueDisplayFilters;
}): TIssueFilterState {
  const richFilters = snapshot.rich_filters ?? {};
  const nextFilters = getDefaultIssueFilters();

  const layout = snapshot.display_filters?.layout;
  if (isIssueLayout(layout)) nextFilters.layout = layout;

  const search = richFilters[DEFAULT_RICH_FILTER_KEYS.search];
  if (typeof search === "string") nextFilters.search = search;

  nextFilters.stateIds = normalizeArrayValue(richFilters[DEFAULT_RICH_FILTER_KEYS.state]);
  nextFilters.assigneeIds = normalizeArrayValue(richFilters[DEFAULT_RICH_FILTER_KEYS.assignees]);

  // A comma-separated string, so a view saved with one priority ("high")
  // reads as that one.
  nextFilters.priorities = normalizeArrayValue(richFilters[DEFAULT_RICH_FILTER_KEYS.priority]);

  const targetDate = richFilters[DEFAULT_RICH_FILTER_KEYS.targetDate];
  if (typeof targetDate === "string") nextFilters.targetDate = targetDate;

  const orderBy = snapshot.display_filters?.order_by;
  if (isIssueOrderBy(orderBy)) nextFilters.orderBy = orderBy;

  return nextFilters;
}

export function buildSavedViewSnapshot(value: {
  rich_filters?: Record<string, unknown>;
  display_filters?: TIssueDisplayFilters;
  display_properties?: TIssueDisplayProperties;
}): TSavedViewSnapshot {
  return {
    richFilters: (normalizePlainValue(value.rich_filters ?? {}) as Record<string, unknown>) ?? {},
    displayFilters:
      (normalizePlainValue(value.display_filters ?? {}) as TIssueDisplayFilters) ?? {},
    displayProperties:
      (normalizePlainValue(value.display_properties ?? {}) as TIssueDisplayProperties) ?? {},
  };
}

export function buildSavedViewSnapshotFromFilterState(
  filters: TIssueFilterState,
  displayProperties: TIssueDisplayProperties,
): TSavedViewSnapshot {
  return buildSavedViewSnapshot({
    rich_filters: cleanQuery({
      [DEFAULT_RICH_FILTER_KEYS.search]: filters.search.trim() || undefined,
      [DEFAULT_RICH_FILTER_KEYS.state]:
        filters.stateIds.length > 0 ? [...filters.stateIds].sort() : undefined,
      [DEFAULT_RICH_FILTER_KEYS.assignees]:
        filters.assigneeIds.length > 0 ? [...filters.assigneeIds].sort() : undefined,
      // Saved as a string, not a list: a view saved with one priority holds
      // "high", so it stays clean, and an older bundle still reads it (NG-3708).
      [DEFAULT_RICH_FILTER_KEYS.priority]:
        filters.priorities.length > 0 ? [...filters.priorities].sort().join(",") : undefined,
      [DEFAULT_RICH_FILTER_KEYS.targetDate]: filters.targetDate || undefined,
    }),
    display_filters: cleanQuery({
      layout: filters.layout,
      ...(filters.layout === "board" ? { group_by: "state" } : {}),
      ...(filters.orderBy !== "-created_at" ? { order_by: filters.orderBy } : {}),
      ...(filters.layout === "calendar" && filters.orderBy === "-created_at"
        ? { order_by: "target_date" }
        : {}),
    }) as TIssueDisplayFilters,
    display_properties: displayProperties,
  });
}

export function stringifySavedViewSnapshot(snapshot: TSavedViewSnapshot): string {
  return JSON.stringify(normalizePlainValue(snapshot));
}

// ---------------------------------------------------------------------------
// DataTable header-control ↔ saved-view snapshot (WorkItemDataTableView).
//
// The shared work-item DataTable drives filtering via TanStack `columnFilters`
// (multi-select + date-range), grouping via a client-side `group_by`, and
// column visibility. "Save as view" snapshots that live header state into a
// view's rich_filters / display_properties; loading a view hydrates it back.
//
// Filters + group_by live in the free-form `rich_filters` bag (the new
// workspace-view detail page is its only consumer — it never replays the bag to
// the server). Column visibility uses the typed `display_properties`. Sort is a
// live URL concern (`?sort=`) and is intentionally NOT persisted — the
// DataTable's column-id sort keys don't map onto the server `order_by` union.
// ---------------------------------------------------------------------------

// Structural subset of WorkItemGroupBy (kept local to avoid a component import).
// Saved views are workspace-scoped (items span many projects), so the
// project-only "stage" dimension is intentionally excluded here.
export type WorkItemViewGroupBy = "state" | "priority" | "assignee" | "category_value";
const WORK_ITEM_GROUP_BY_KEYS = new Set<string>([
  "state",
  "priority",
  "assignee",
  // The managed Category VALUE (NG-422). Unlike "stage" this one IS
  // workspace-wide — the category field is workspace-scoped, not per-process —
  // so a saved view spanning projects can round-trip it.
  "category_value",
]);

export interface WorkItemViewHeaderState {
  search: string;
  columnFilters: ColumnFiltersState;
  // Accepted for forward-compat; not persisted (see header comment).
  sorting?: SortingState;
  groupBy: WorkItemViewGroupBy | null;
  columnVisibility: Record<string, boolean>;
}

function headerMultiValue(filters: ColumnFiltersState, id: string): string[] {
  const found = filters.find((f) => f.id === id)?.value as
    | { type: string; value: string[] }
    | undefined;
  return found?.type === "multi" ? found.value : [];
}

function headerRangeValue(filters: ColumnFiltersState, id: string): { from?: string; to?: string } {
  const found = filters.find((f) => f.id === id)?.value as
    | { type: string; value: { from?: string; to?: string } }
    | undefined;
  return found?.type === "dateRange" ? found.value : {};
}

export function buildViewSnapshotFromHeaderState(
  state: WorkItemViewHeaderState,
): TSavedViewSnapshot {
  const start = headerRangeValue(state.columnFilters, "start");
  const target = headerRangeValue(state.columnFilters, "target");
  const completed = headerRangeValue(state.columnFilters, "completed");
  const vis = state.columnVisibility;
  return buildSavedViewSnapshot({
    rich_filters: cleanQuery({
      search: state.search.trim() || undefined,
      state: headerMultiValue(state.columnFilters, "state"),
      priority: headerMultiValue(state.columnFilters, "priority"),
      assignees: headerMultiValue(state.columnFilters, "assignee"),
      start_date_after: start.from || undefined,
      start_date_before: start.to || undefined,
      target_date_after: target.from || undefined,
      target_date_before: target.to || undefined,
      // DAYS, as the header holds them (NG-3491). The table turns them into the
      // instants the server reads, in the workspace's zone, at load.
      completed_date_after: completed.from || undefined,
      completed_date_before: completed.to || undefined,
      group_by: state.groupBy ?? undefined,
    }),
    display_filters: {} as TIssueDisplayFilters,
    display_properties: {
      show_state: vis.state ?? true,
      show_priority: vis.priority ?? true,
      show_assignee: vis.assignee ?? true,
      show_target_date: vis.target ?? vis.start ?? true,
      show_predecessors: vis.predecessors ?? true,
      show_identifier: true,
    },
  });
}

/**
 * Rebuild the header controls a saved view was captured with.
 *
 * Column visibility is deliberately NOT returned: the detail page applies a view's
 * `display_properties` by hydrating them into the issue-view-preferences store,
 * which is the single reader the table takes visibility from and which maps them
 * through `COLUMN_FLAG`. Deriving them a second time here drifted from that map
 * (it read `start` off `show_target_date`, which NG-422 had split apart) — NG-528.
 */
export function hydrateHeaderStateFromView(view: {
  rich_filters?: Record<string, unknown>;
  display_filters?: TIssueDisplayFilters;
  display_properties?: TIssueDisplayProperties;
}): Omit<WorkItemViewHeaderState, "columnVisibility"> {
  const rf = view.rich_filters ?? {};
  const filters: ColumnFiltersState = [];

  const pushMulti = (id: string, raw: unknown) => {
    // normalizeArrayValue coerces a legacy scalar ("high") and CSV ("a,b") into
    // a sorted string[], so old saved views still hydrate.
    const value = normalizeArrayValue(raw);
    if (value.length > 0) filters.push({ id, value: { type: "multi", value } });
  };
  pushMulti("state", rf.state);
  pushMulti("priority", rf.priority);
  pushMulti("assignee", rf.assignees);

  const pushDateRange = (id: string, rawFrom: unknown, rawTo: unknown) => {
    const from = typeof rawFrom === "string" ? rawFrom : "";
    const to = typeof rawTo === "string" ? rawTo : "";
    if (from || to) filters.push({ id, value: { type: "dateRange", value: { from, to } } });
  };
  pushDateRange("start", rf.start_date_after, rf.start_date_before);
  // Legacy saved views stored a single scalar `target_date`; treat it as an
  // upper bound so they still hydrate sensibly.
  pushDateRange(
    "target",
    rf.target_date_after,
    typeof rf.target_date_before === "string" ? rf.target_date_before : rf.target_date,
  );
  pushDateRange("completed", rf.completed_date_after, rf.completed_date_before);

  const groupBy =
    typeof rf.group_by === "string" && WORK_ITEM_GROUP_BY_KEYS.has(rf.group_by)
      ? (rf.group_by as WorkItemViewGroupBy)
      : null;

  return {
    search: typeof rf.search === "string" ? rf.search : "",
    columnFilters: filters,
    groupBy,
  };
}
